import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { localSocket, remoteSocket } from './services/socket';
import ActivationPage from './pages/ActivationPage';
import LauncherPage from './pages/LauncherPage';
import SelectGame from './pages/SelectGame';
import { BrandingProvider } from './branding/BrandingContext';

const SystemErrorOverlay = ({ title, message }: { title: string; message: string }) => {
  const [remoteConnected, setRemoteConnected] = useState(remoteSocket.connected);
  const [localConnected, setLocalConnected] = useState(localSocket.connected);

  useEffect(() => {
    const checkInterval = setInterval(() => {
      setRemoteConnected(remoteSocket.connected);
      setLocalConnected(localSocket.connected);
    }, 1000);

    return () => clearInterval(checkInterval);
  }, []);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#05070a] font-['Born_Strong'] p-6">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_#0d141f_0%,_#05070a_100%)]" />
      <div className="relative z-10 w-full max-w-4xl">
        <div className="bg-[#0c111a] border border-white/10 rounded-2xl p-10 shadow-2xl">
          <div className="flex justify-between items-start mb-8 border-b border-white/5 pb-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-[0.3em] text-red-600 italic">Diagnostic Alert</span>
              </div>
              <h1 className="text-4xl font-black text-white uppercase italic tracking-normal">{title}</h1>
            </div>
            <div className="flex gap-8">
              <div className="text-right">
                <span className="block text-[10px] text-white/30 font-bold uppercase tracking-widest mb-1">Remote Uplink</span>
                <span className={`text-xl font-black uppercase italic ${remoteConnected ? 'text-emerald-500' : 'text-red-600'}`}>
                  {remoteConnected ? 'Active' : 'Offline'}
                </span>
              </div>
              <div className="text-right border-l border-white/10 pl-8">
                <span className="block text-[10px] text-white/30 font-bold uppercase tracking-widest mb-1">Local Bridge</span>
                <span className={`text-xl font-black uppercase italic ${localConnected ? 'text-emerald-500' : 'text-red-600'}`}>
                  {localConnected ? 'Active' : 'Offline'}
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white/[0.02] border-l-4 border-red-600 p-6 mb-8">
            <p className="text-xl text-slate-300 font-medium leading-relaxed uppercase italic tracking-wide">{message}</p>
          </div>
          <div className="flex items-center justify-between opacity-60">
            <div className="flex items-center gap-4">
              <div className="flex gap-1.5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-1.5 h-6 bg-red-600 animate-pulse" style={{ animationDelay: `${i * 0.2}s` }} />
                ))}
              </div>
              <span className="text-sm font-black text-white uppercase italic tracking-[0.2em]">Recovery in progress...</span>
            </div>
            <span className="text-[10px] text-white/20 font-bold uppercase tracking-[0.4em]">Node: {window.location.hostname}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [systemError, setSystemError] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    const handleFailure = (e: any) => setSystemError(e.detail);
    const handleRecovery = () => setSystemError(null);
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();

    window.addEventListener('system_failure', handleFailure);
    window.addEventListener('system_recovered', handleRecovery);
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('system_failure', handleFailure);
      window.removeEventListener('system_recovered', handleRecovery);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  return (
    <BrandingProvider>
      <Router>
        <style dangerouslySetInnerHTML={{ __html: `
          * { cursor: none !important; user-select: none; }
          html, body {
            background-color: #05070a;
            margin: 0;
            padding: 0;
            overflow: hidden;
            font-family: 'Quantico', sans-serif;
            font-weight: bold;
          }
        `}} />

        {systemError && <SystemErrorOverlay title={systemError.title} message={systemError.message} />}

        <div id="root-container" className="h-screen w-screen overflow-hidden bg-[#05070a] relative">
          <Routes>
            <Route path="/" element={<LauncherPage />} />
            <Route path="/game" element={<LauncherPage />} />
            <Route path="/select-game" element={<SelectGame />} />
            <Route path="/activation" element={<ActivationPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </BrandingProvider>
  );
};

export default App;
